import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { cn } from '@/lib/utils'
import { Column } from '@tanstack/react-table'
import { ChatHistoryTableDef, HistoryWorkflowStatus } from '../chat-history'
import React from 'react'
import { Label } from '@/components/ui/label'
import { WorkflowStatus } from '@/hooks/api/use-all-workflows-query'

export type CHStatusFilterValue = Exclude<
  WorkflowStatus,
  | WorkflowStatus.Queued
  | WorkflowStatus.Planning
  | WorkflowStatus.ValidationError
  | WorkflowStatus.Cancelled
>

export interface CHStatusFilterOption {
  value: CHStatusFilterValue
  label: string
}

interface CHStatusColumnFilterProps {
  column: Column<ChatHistoryTableDef>
  openFilter: boolean
  setOpenFilter: SetState<boolean>
}

const CH_STATUS_FILTER_OPTIONS: CHStatusFilterOption[] = [
  { value: WorkflowStatus.Success, label: 'Success' },
  { value: WorkflowStatus.InProgress, label: 'In Progress' },
  { value: WorkflowStatus.Failure, label: 'Failure' },
]

export function CHStatusColumnFilter({ column, setOpenFilter }: CHStatusColumnFilterProps) {
  const filterValue = React.useMemo(
    () => (column.getFilterValue() ?? '') as string,
    [column.getFilterValue()],
  )

  const [selectedStatuses, setSelectedStatuses] = React.useState<CHStatusFilterValue[]>(() => {
    return filterValue ? (filterValue?.split(',') as CHStatusFilterValue[]) : []
  })

  const handleKeyboardInteraction = (e: React.KeyboardEvent, action: () => void) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      action()
    }
  }

  const isAllSelected = React.useMemo(
    () => selectedStatuses?.length === CH_STATUS_FILTER_OPTIONS.length,
    [selectedStatuses],
  )
  const hasChanges = React.useMemo(() => {
    return selectedStatuses.length > 0 && selectedStatuses.join(',') !== column.getFilterValue()
  }, [selectedStatuses, filterValue])

  const onSelectAll = React.useCallback((checked: boolean) => {
    setSelectedStatuses(checked ? CH_STATUS_FILTER_OPTIONS.map((option) => option.value) : [])
  }, [])

  const onApplyFilter = React.useCallback(() => {
    column.setFilterValue(selectedStatuses.join(','))
    setOpenFilter(false)
  }, [selectedStatuses])

  const onClearFilter = React.useCallback(() => {
    setSelectedStatuses([])
    column.setFilterValue('')
    setOpenFilter(false)
  }, [])

  const onStatusToggle = React.useCallback((value: CHStatusFilterValue) => {
    setSelectedStatuses((prevFilters) =>
      prevFilters.includes(value)
        ? prevFilters.filter((filter) => filter !== value)
        : [...prevFilters, value],
    )
  }, [])

  return (
    <div className='flex w-[240px] flex-col gap-1 p-1.5'>
      {/* Select All Toggle */}
      <div className='flex items-center gap-4 px-1 py-3'>
        <Checkbox
          id='selectAll'
          checked={isAllSelected}
          onCheckedChange={onSelectAll}
          variant='neutral'
        />
        <Label variant={'checkbox'} htmlFor='selectAll' className='w-full cursor-pointer text-sm'>
          Select All
        </Label>
      </div>

      {/* Status Filter Options */}
      <>
        {CH_STATUS_FILTER_OPTIONS.map((option) => (
          <div
            key={option.value}
            role='button'
            tabIndex={0}
            onKeyDown={(e) => handleKeyboardInteraction(e, () => onStatusToggle(option.value))}
            onClick={() => onStatusToggle(option.value)}
            className={cn(
              'flex cursor-pointer items-center gap-4 rounded px-5 py-3',
              selectedStatuses?.includes(option.value) && 'bg-neutral-50',
            )}
          >
            <Checkbox checked={selectedStatuses.includes(option.value)} variant='neutral' />
            <HistoryWorkflowStatus status={option.value} showIcon={false} />
          </div>
        ))}
      </>

      {/* Filter Actions */}
      <div className='flex items-center justify-end gap-3 px-1 py-2'>
        <Button
          size={'xs'}
          onClick={onClearFilter}
          disabled={selectedStatuses.length === 0}
          className='disabled:opacity-50'
          unstyled
        >
          Reset
        </Button>
        <Button size='xs' onClick={onApplyFilter} disabled={!hasChanges} className='normal-case'>
          Apply Filters
        </Button>
      </div>
    </div>
  )
}

// Export the options for use in parent components
export { CH_STATUS_FILTER_OPTIONS }
